import classNames from 'classnames'
import { graphql, useStaticQuery } from 'gatsby'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'

import { useGa4Config } from '../hooks/useGa4Config'
import { MeasurLocale, localeLanguage } from '../locale/i18n'
import { ISlugLink } from '../locale/pathing'
import { ContentGroups } from '../logic/ga4'
import { BreadcrumbPart } from '../logic/ld-json'
import { capturePageMount } from '../logic/metrics'
import publicConfig from '../logic/public-config'

import Breadcrumbs from './Breadcrumbs'
import Footer from './Footer'
import TopNav from './nav/TopNav'

import './Page.scss'

interface Props {
  breadcrumbParts?: null
  children: React.ReactNode
  isGray?: boolean
  localisedSlugs: ISlugLink[]
  whitelistedLocales?: MeasurLocale[]
  contentGroup?: ContentGroups
}

interface WithBreadcrumbs extends Omit<Props, 'breadcrumbParts'> {
  breadcrumbParts: BreadcrumbPart[]
  siteUrl: string
}

const Page = ({
  children,
  isGray,
  localisedSlugs,
  whitelistedLocales,
  contentGroup,
  ...props
}: Props | WithBreadcrumbs) => {
  useGa4Config(contentGroup)
  useEffect(() => {
    capturePageMount()
  }, [])

  const {
    site: {
      siteMetadata: { siteUrl },
    },
  } = useStaticQuery<{
    site: {
      siteMetadata: {
        siteUrl: string
      }
    }
  }>(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `)

  return (
    <div className={classNames('page', { 'page--gray': isGray })}>
      <Helmet
        link={localisedSlugs
          .filter(
            ls =>
              !whitelistedLocales || whitelistedLocales?.includes(ls.locale),
          )
          .map(alt => ({
            href: siteUrl + alt.slug,
            hrefLang: localeLanguage(alt.locale),
            rel: 'alternate',
          }))}
      />
      {Boolean(publicConfig.bannerText) && (
        <div className="page__banner">
          <div role="banner" className="gutter gutter--center">
            {publicConfig.bannerText}
          </div>
        </div>
      )}
      <TopNav localisedSlugs={localisedSlugs} />
      <main className="page__main">
        {props.breadcrumbParts && (
          <Breadcrumbs
            className="gutter gutter--center"
            parts={props.breadcrumbParts}
            siteUrl={props.siteUrl}
          />
        )}
        {children}
      </main>
      <Footer />
    </div>
  )
}
export default Page
